import React, {useState} from "react";
import styles from "./StepVerification.module.scss";
import IdentificationLegalEntityBorrower
  from "../../../../../../../components/user/identification/IdentificationLegalEntityBorrower";
import {useDispatch} from "react-redux";
import {
  BorrowerType,
  Identification as ApiIdentification,
  StepStatus,
  UuidResult
} from "../../../../../../../api-client";
import {useSearchParams} from "react-router-dom";
import {useEffectOnMount, useProfileData} from "../../../../../../../hooks";
import {createIdentificationManager} from "../../../../../../../di";
import {setIdentificationDraft} from "../../../../../../../store/profile";
import {Button, Image, Loader, VerificationConfirmationModal, VerificationSMSModal} from "../../../../../../../components";
import {useWizardContext} from "../../../context";
import {WizardStep} from "../../../types";
import clockIcon from "assets/images/svg/clock.svg";
import stepApprovedIcon from "assets/images/svg/step-approved.svg";

const StepVerification = () => {
  const {backendCurrentStep, onboardingStepStatus, setActiveStep, onboardingStepNote, refreshOnboardingState, isInitialDataLoaded, prescoreDraft} = useWizardContext();
  const profileData = useProfileData();
  const dispatch = useDispatch();
  const [type, setType] = useState(BorrowerType.LEGAL_ENTITY);
  const [_, setSearchParams] = useSearchParams();
  const [identification, setIdentification] = useState<ApiIdentification | undefined>(undefined);
  const isVerified = () => {
    return profileData.isVerified;
  };
  const [smsModalOpened, setSmsModalOpened] = useState(false);
  const [confirmationModalOpened, setConfirmationModalOpened] = useState(false);
  const [isFillingNewForm, setIsFillingNewForm] = useState(false);
  const [hasInteractedWithForm, setHasInteractedWithForm] = useState(false);

  const isIdentificationWaitingApproval =
    backendCurrentStep === WizardStep.VERIFICATION &&
    onboardingStepStatus === StepStatus.WAITING_APPROVAL;

  const isIdentificationRejected =
    backendCurrentStep === WizardStep.VERIFICATION &&
    onboardingStepStatus === StepStatus.REJECTED &&
    !isFillingNewForm;

  const isIdentificationVerified =
    backendCurrentStep !== null && backendCurrentStep > WizardStep.VERIFICATION;

  useEffectOnMount(() => {
    if (isVerified()) {
      if (profileData.isLegalEntity) setType(BorrowerType.LEGAL_ENTITY);
      const callback = async () => {
        try {
          const provider = await createIdentificationManager();
          const identification = await provider.findOne();
          setIdentification(identification);
        } catch (e: any) {
          console.log(e.message);
        }
      };
      callback();
    }
  });

  const handleSubmit = (uuid: UuidResult) => {
    dispatch(setIdentificationDraft(true));
    setSearchParams({uuid: uuid.uuid});
    setSmsModalOpened(true);
    return;
  };

  const isProfileDataLoaded = profileData.uuid !== undefined;

  const renderContent = () => {
    if (!isInitialDataLoaded || backendCurrentStep === null || !isProfileDataLoaded) {
      return (
        <div className={styles.loaderWrapper}>
          <Loader size="large" overlay={false} />
        </div>
      );
    }

    if (isIdentificationWaitingApproval) {
      return (
        <div className={styles.notificationContainer}>
          <div className={styles.waitingApprovalWrapper}>
            <div className={styles.waitingApprovalCard}>
              <h2 className={styles.waitingApprovalTitle}>Анкета на рассмотрении</h2>
              <div className={styles.waitingApprovalRow}>
                <Image src={clockIcon} className={styles.waitingApprovalIcon} alt="" aria-hidden />
                <p className={styles.waitingApprovalText}>
                Обычно рассмотрение занимает 1-2 рабочих дня
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (isIdentificationRejected) {
      const note = onboardingStepNote?.trim() || "—";
      const rejectionMessage =
        `Причина отклонения: ${note}. Вы можете повторно заполнить анкету.`;

      return (
        <div className={styles.notificationContainer}>
          <div className={styles.rejectedWrapper}>
            <div className={styles.rejectedCard}>
              <h2 className={styles.rejectedTitle}>Анкета отклонена</h2>
              <p className={styles.rejectedText}>{rejectionMessage}</p>
              <Button
                type="button"
                className={styles.rejectedButton}
                onClick={() => setIsFillingNewForm(true)}
              >
              Заполнить повторно
              </Button>
            </div>
          </div>
        </div>
      );
    }

    if (isIdentificationVerified) {
      return (
        <div className={styles.notificationContainer}>

          <div className={styles.approvedWrapper}>
            <div className={styles.approvedCard}>
              <Image src={stepApprovedIcon} className={styles.approvedIcon} alt="" aria-hidden />
              <div className={styles.approvedContent}>
                <h2 className={styles.approvedTitle}>Верификационная анкета одобрена</h2>
                <p className={styles.approvedText}>
                Данные Вашей анкеты приняты. Вы можете перейти к следующему шагу.
                </p>
              </div>
              <Button
                type="button"
                className={styles.approvedButton}
                onClick={() => setActiveStep(WizardStep.SCORING)}
              >
              Перейти к следующему шагу
              </Button>
            </div>
          </div>
        </div>
      );
    }

    const isFirstStepPassed = backendCurrentStep != null && backendCurrentStep >= WizardStep.VERIFICATION;
    const shouldHighlightNotification = !isFirstStepPassed && hasInteractedWithForm;

    const handleFormInteraction = () => {
      if (!isFirstStepPassed) {
        setHasInteractedWithForm(true);
      }
    };

    return (
      <>
        {!isFirstStepPassed && (
          <div className={shouldHighlightNotification ? styles.infoCardHighlighted: styles.infoCard}>
            <div className={styles.infoIcon}>
              <span>!</span>
            </div>
            <p className={styles.infoText}>
              Заполнение верификационной анкеты доступно после прохождения pre-scoring
            </p>
          </div>
        )}
        <div className={styles.quizWrapper}>
          {!isFirstStepPassed && (
            <div
              className={styles.formOverlay}
              onClick={handleFormInteraction}
              onKeyDown={handleFormInteraction}
              role="presentation"
              aria-hidden
            />
          )}
          <div {...(isFirstStepPassed ? {} : {inert: true})} className={styles.formContainer}>
            <IdentificationLegalEntityBorrower
              onSubmit={handleSubmit}
              identification={identification}
              submitDisabled={!isFirstStepPassed}
              initialInnLegalEntity={prescoreDraft.borrowerInn}
            />
          </div>
        </div>
      </>
    );
  };

  return (
    <div className={styles.stepVerification}>
      {renderContent()}
      <VerificationSMSModal
        active={smsModalOpened}
        onClose={() => setSmsModalOpened(false)}
        setConfirmationModalOpened={setConfirmationModalOpened}
      />
      <VerificationConfirmationModal
        active={confirmationModalOpened}
        onClose={() => setConfirmationModalOpened(false)}
        onCloseWithRefresh={refreshOnboardingState}
      />
    </div>
  );
};

export default StepVerification;